/**
 * TransactionClient - TypeScript SDK for the Transaction Monitoring service
 *
 * Provides a thin wrapper around the TM service endpoints.  Currently only the
 * creation endpoint is implemented, but additional helpers (list, getById,
 * etc.) can be added later.
 */

import { BaseClient } from '../core/client';
import type { BaseClientConfig, RequestOptions } from '../core/config';
import type { TransactionCreateDTO, TransactionCreateResponse } from './types';

export class TransactionClient extends BaseClient {
  constructor(config: BaseClientConfig) {
    // no special config needed; simply forward to BaseClient
    super(config);
  }

  // ==========================================================================
  // Transaction creation
  // ==========================================================================

  /**
   * Submit a new transaction record to the monitoring service.
   *
   * The gateway endpoint is `POST /api/v1/tm/transactions` and returns 201
   * with no body on success.  The SDK method resolves to `void` to reflect
   * that behaviour.
   *
   * @param request - Data required to create the transaction
   * @param requestOptions - Optional request options (e.g. AbortSignal)
   *
   * @example
   * ```ts
   * const client = new TransactionClient({
   *   baseURL: process.env.API_GATEWAY_URL!,
   *   tenantId: 'tenant-123',
   *   apiKey: 'pk_test_...',
   * });
   *
   * await client.createTransaction({
   *   tx_id: 'tx-1',
   *   reference: 'ref-1',
   *   tenant_id: 'tenant-123',
   *   customer_id: 'cust-1',
   *   transaction_type: 'deposit',
   *   transaction_mode: 'ach',
   *   amount: '100.00',
   *   currency: 'USD',
   *   status: 'pending',
   *   source_account: 'acct-1',
   *   destination_account: 'acct-2',
   *   country: 'US',
   *   ip_address: '10.0.0.1',
   *   metadata: {},
   *   benificiary_comment: '',
   *   transaction_date: new Date().toISOString(),
   * });
   * ```
   */
  async createTransaction(
    request: TransactionCreateDTO,
    requestOptions?: RequestOptions
  ): Promise<TransactionCreateResponse> {
    const data = await this.requestWithRetry<TransactionCreateResponse>('/api/v1/tm/transactions', {
      method: 'POST',
      body: JSON.stringify(request),
    }, undefined, undefined, requestOptions);
    return data
  }

  /**
   * 
   * @param transactionId tx_id of the transaction 
   * @param requestOptions  optional request options (e.g. AbortSignal)
   */
  async getTransaction(transactionId: string, requestOptions?: RequestOptions): Promise<void> {
    await this.requestWithRetry<void>(`/api/v1/tm/transactions/status/${transactionId}`, {
      method: 'GET',
    }, undefined, undefined, requestOptions);
  }
}

